import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { canWith } from '../utils/rbacPermissions';
import {
  addPostComment,
  createFeedPost,
  listFeedPosts,
  listPostComments,
  removePostReaction,
  setPostReaction,
} from '../services/feedService';
import { postFlowIaChat } from '../services/flowIaService';
import { formatMsg } from '../utils/formatMsg';

const REACTIONS = [
  { type: 'like', labelKey: 'feedWall.reactLike' },
  { type: 'celebrate', labelKey: 'feedWall.reactCelebrate' },
  { type: 'support', labelKey: 'feedWall.reactSupport' },
];

function relativeTime(iso, t, locale) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('time.relative.justNow');
  if (mins < 60) return formatMsg(t('time.relative.minutesAgo'), { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-AR', {
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso));
}

function audienceLabel(t, type) {
  if (type === 'team') return t('feedWall.audienceTeam');
  if (type === 'department') return t('feedWall.audienceDepartment');
  return t('feedWall.audienceOrg');
}

function postKindLabel(t, kind) {
  if (kind === 'recognition') return t('feedWall.kindRecognition');
  if (kind === 'celebration') return t('feedWall.kindCelebration');
  if (kind === 'event') return t('feedWall.kindEvent');
  if (kind === 'communication') return t('feedWall.kindCommunication');
  return t('feedWall.kindNormal');
}

function FeedComposer({ t, locale, canCreate, onPosted }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('organization');
  const [busy, setBusy] = useState(false);
  const [iaBusy, setIaBusy] = useState(false);
  const [error, setError] = useState(null);

  const publish = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      await createFeedPost({ body: text, audience_type: audience, post_kind: 'normal' });
      setBody('');
      setOpen(false);
      onPosted();
    } catch {
      setError(t('feedWall.errorPublish'));
    } finally {
      setBusy(false);
    }
  };

  const improveWithFlowIa = async () => {
    const text = body.trim();
    if (!text) return;
    setIaBusy(true);
    setError(null);
    try {
      const res = await postFlowIaChat({
        screen: 'feed',
        question: `${t('feedWall.flowIaImprovePrompt')}\n\n${text.slice(0, 2000)}`,
        locale,
        context: { draft: text.slice(0, 2000) },
        history: [],
      });
      const improved = (res?.answer || '').trim();
      if (improved) setBody(improved);
    } catch {
      setError(t('feedWall.errorFlowIa'));
    } finally {
      setIaBusy(false);
    }
  };

  if (!canCreate) return null;

  return (
    <section className="feed-composer panel panel--elevated" aria-label={t('feedWall.composerAria')}>
      {!open ? (
        <button type="button" className="feed-composer__trigger" onClick={() => setOpen(true)}>
          {t('feedWall.composerPlaceholder')}
        </button>
      ) : (
        <div className="feed-composer__expanded">
          <textarea
            className="input feed-composer__input"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('feedWall.composerPlaceholder')}
          />
          <div className="feed-composer__row">
            <label className="feed-composer__audience">
              <span className="text-sm text-muted">{t('feedWall.audienceLabel')}</span>
              <select className="input" value={audience} onChange={(e) => setAudience(e.target.value)}>
                <option value="organization">{t('feedWall.audienceOrg')}</option>
                <option value="team">{t('feedWall.audienceTeam')}</option>
                <option value="department">{t('feedWall.audienceDepartment')}</option>
              </select>
            </label>
            <button type="button" className="btn btn--ghost btn--sm" disabled={iaBusy || !body.trim()} onClick={improveWithFlowIa}>
              {t('feedWall.flowIaImprove')}
            </button>
          </div>
          {error ? <p className="panel__muted text-sm">{error}</p> : null}
          <div className="feed-composer__actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
              {t('employees.cancel')}
            </button>
            <button type="button" className="btn btn--primary btn--sm" disabled={busy || !body.trim()} onClick={publish}>
              {t('feedWall.publish')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function PostCard({ item, t, locale, canReact, canComment, onRefresh }) {
  const { post, author, reaction_summaries: reactions, comment_count: commentCount } = item;
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [totalComments, setTotalComments] = useState(commentCount);

  const loadComments = useCallback(async (nextSkip = 0) => {
    setCommentsLoading(true);
    try {
      const r = await listPostComments(post.id, { skip: nextSkip, limit: 5 });
      const page = r.data;
      setTotalComments(page.total || 0);
      if (nextSkip === 0) setComments(page.items || []);
      else setComments((prev) => [...prev, ...(page.items || [])]);
      setSkip(nextSkip + (page.items?.length || 0));
    } finally {
      setCommentsLoading(false);
    }
  }, [post.id]);

  const toggleComments = () => {
    const next = !commentsOpen;
    setCommentsOpen(next);
    if (next && comments.length === 0) loadComments(0);
  };

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    await addPostComment(post.id, { body: text });
    setCommentText('');
    await loadComments(0);
    onRefresh();
  };

  const toggleReaction = async (type, active) => {
    if (!canReact) return;
    if (active) await removePostReaction(post.id, type);
    else await setPostReaction(post.id, type);
    onRefresh();
  };

  const initials = (author.name || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <article className="feed-post panel panel--elevated">
      <header className="feed-post__head">
        <div className="feed-post__avatar" aria-hidden>
          {initials}
        </div>
        <div className="feed-post__meta">
          {post.post_kind && post.post_kind !== 'normal' ? (
            <span className={`feed-post__kind feed-post__kind--${post.post_kind}`}>{postKindLabel(t, post.post_kind)}</span>
          ) : null}
          <strong className="feed-post__name">{author.name}</strong>
          {(author.position || author.area) && (
            <span className="feed-post__role text-muted text-sm">
              {[author.position, author.area].filter(Boolean).join(' · ')}
            </span>
          )}
          <span className="feed-post__time text-muted text-sm">
            {relativeTime(post.updated_at !== post.created_at ? post.updated_at : post.created_at, t, locale)}
            {' · '}
            {audienceLabel(t, post.audience_type)}
          </span>
        </div>
        {post.is_pinned ? <span className="feed-post__pin">{t('feedWall.pinned')}</span> : null}
      </header>
      <p className="feed-post__body">{post.body}</p>
      <div className="feed-post__reactions">
        {REACTIONS.map((r) => {
          const sum = reactions.find((x) => x.reaction_type === r.type) || { count: 0, active: false };
          return (
            <button
              key={r.type}
              type="button"
              className={`feed-react-btn${sum.active ? ' is-active' : ''}`}
              disabled={!canReact}
              onClick={() => toggleReaction(r.type, sum.active)}
            >
              {t(r.labelKey)} {sum.count > 0 ? sum.count : ''}
            </button>
          );
        })}
        <button type="button" className="feed-react-btn feed-react-btn--comment" onClick={toggleComments}>
          {t('feedWall.comment')} {commentCount > 0 ? commentCount : ''}
        </button>
      </div>
      {commentsOpen ? (
        <div className="feed-post__comments">
          {commentsLoading && comments.length === 0 ? <p className="panel__muted text-sm">{t('common.loading')}</p> : null}
          <ul className="feed-comments-list">
            {comments.map((c) => (
              <li key={c.id} className="feed-comments-list__item">
                <span className="feed-comments-list__body">{c.body}</span>
              </li>
            ))}
          </ul>
          {skip < totalComments ? (
            <button type="button" className="btn btn--ghost btn--sm" disabled={commentsLoading} onClick={() => loadComments(skip)}>
              {t('feedWall.moreComments')}
            </button>
          ) : null}
          {canComment ? (
            <div className="feed-comment-form">
              <input
                className="input"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t('feedWall.commentPlaceholder')}
              />
              <button type="button" className="btn btn--primary btn--sm" onClick={submitComment}>
                {t('feedWall.commentSubmit')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export function FeedWall() {
  const { t, locale, permSummary, isProductionUi } = useApp();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const canView = canWith(permSummary, 'feed', 'view', isProductionUi);
  const canCreate = canWith(permSummary, 'feed', 'create', isProductionUi);
  const canComment = canWith(permSummary, 'feed', 'comment', isProductionUi);
  const canReact = canWith(permSummary, 'feed', 'react', isProductionUi);

  const load = useCallback(async (append = false, cur = null) => {
    if (!canView) {
      setLoading(false);
      return;
    }
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setError(false);
    }
    try {
      const r = await listFeedPosts({ cursor: append ? cur : undefined });
      const page = r.data;
      setCursor(page.next_cursor || null);
      setItems((prev) => (append ? [...prev, ...(page.items || [])] : page.items || []));
    } catch {
      setError(true);
      if (!append) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [canView]);

  useEffect(() => {
    load(false);
  }, [load]);

  if (!canView) {
    return (
      <div className="empty-state empty-state--premium">
        <p>{t('feedWall.forbidden')}</p>
      </div>
    );
  }

  return (
    <div className="feed-wall page-stack">
      <header className="feed-wall__head">
        <h1 className="feed-wall__title">{t('feedWall.title')}</h1>
        <p className="panel__muted">{t('feedWall.lead')}</p>
      </header>

      <div className="feed-wall__layout">
        <div className="feed-wall__main">
          <FeedComposer t={t} locale={locale} canCreate={canCreate} onPosted={() => load(false)} />

          {loading ? (
            <p className="panel__muted">{t('common.loading')}</p>
          ) : error ? (
            <div className="empty-state empty-state--boxed">
              <p>{t('feedWall.errorLoad')}</p>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => load(false)}>
                {t('common.retry')}
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="empty-state empty-state--boxed feed-wall__empty">
              <p>{t('feedWall.empty')}</p>
              {canCreate ? (
                <button type="button" className="btn btn--primary btn--sm" onClick={() => document.querySelector('.feed-composer__trigger')?.click()}>
                  {t('feedWall.emptyCta')}
                </button>
              ) : null}
            </div>
          ) : (
            <>
              {items.map((item) => (
                <PostCard
                  key={item.post.id}
                  item={item}
                  t={t}
                  locale={locale}
                  canReact={canReact}
                  canComment={canComment}
                  onRefresh={() => load(false)}
                />
              ))}
              {cursor ? (
                <button type="button" className="btn btn--secondary" disabled={loadingMore} onClick={() => load(true, cursor)}>
                  {loadingMore ? t('common.loading') : t('feedWall.loadMore')}
                </button>
              ) : null}
            </>
          )}
        </div>

        <aside className="feed-wall__aside panel panel--elevated">
          <h2 className="dash-section-title dash-section-title--compact">{t('feedWall.asideTitle')}</h2>
          <ul className="feed-aside-links">
            <li>
              <Link to="/communications">{t('feedWall.asideComms')}</Link>
            </li>
            <li>
              <Link to="/awards">{t('feedWall.asideCelebrations')}</Link>
            </li>
            <li>
              <Link to="/employee-portal">{t('feedWall.asidePortal')}</Link>
            </li>
          </ul>
          <p className="panel__muted text-sm">{t('feedWall.asideHint')}</p>
        </aside>
      </div>
    </div>
  );
}
